/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
// Sección "Comprobante fiscal" del alta de gasto — Libro IVA compras (Wave 4).
// Solo la monta el padre cuando la org es Responsable Inscripto.
// Estado: ExpenseFiscalDraft controlado por ExpenseRegisterModal.
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { ModalFormSection } from '../../shared/modals/ModalFormSection';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { isValidCuitDigits, normalizeCuitDigits } from '../../fiscal/domain-api';
import { consultaPadron } from '../../fiscal/infra-api';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { typography } from '../../shared/theme/typography';
import { formatMoney } from '../../shared/format/public-api';
import { ExpenseFormChipRow } from './ExpenseFormChipRow';
import {
  docTypeDiscriminatesIva,
  expenseFiscalDraftTotal,
  PURCHASE_DOC_TYPES,
  purchaseDocType,
  type ExpenseFiscalDraft,
  type PurchaseDocClass,
  type PurchaseDocKind,
} from '../domain/expenseFiscalInvoice';

const DOC_CLASSES: PurchaseDocClass[] = ['A', 'B', 'C', 'M'];
const KIND_OPTIONS: Array<{ id: PurchaseDocKind; label: string }> = [
  { id: 'factura', label: 'Factura' },
  { id: 'nota_debito', label: 'Nota de débito' },
  { id: 'nota_credito', label: 'Nota de crédito' },
];

const docCodeFor = (docClass: PurchaseDocClass, kind: PurchaseDocKind): number | null =>
  PURCHASE_DOC_TYPES.find((t) => t.docClass === docClass && t.kind === kind)?.code ?? null;

const ALICUOTA_OPTIONS = [
  { id: '5', rateId: 5, label: '21 %' },
  { id: '4', rateId: 4, label: '10,5 %' },
  { id: '6', rateId: 6, label: '27 %' },
  { id: '3', rateId: 3, label: '0 %' },
  { id: '8', rateId: 8, label: '5 %' },
  { id: '9', rateId: 9, label: '2,5 %' },
];

type Props = {
  draft: ExpenseFiscalDraft;
  onChange: (draft: ExpenseFiscalDraft) => void;
  /** Monto del gasto para el warning de desfasaje con el total del comprobante. */
  expenseAmount: number | null;
  submitting: boolean;
  /** Sucursal activa — habilita el botón "Consultar ARCA" del CUIT del emisor. */
  branchId?: string | null;
  testIDPrefix?: string;
};

const inputStyle = modalExperience.formInput;

export function ExpenseFiscalSection({
  draft,
  onChange,
  expenseAmount,
  submitting,
  branchId = null,
  testIDPrefix = 'gastos.new.fiscal',
}: Props) {
  const patch = (p: Partial<ExpenseFiscalDraft>) => onChange({ ...draft, ...p });
  const hasDoc = draft.docType != null;
  const [consultingArca, setConsultingArca] = useState(false);
  const [arcaError, setArcaError] = useState<string | null>(null);
  const [arcaFound, setArcaFound] = useState<string | null>(null);

  const handleConsultarArca = async () => {
    if (!branchId) {
      setArcaError('Seleccioná una sucursal activa.');
      return;
    }
    if (!isValidCuitDigits(draft.supplierTaxId)) {
      setArcaError('Ingresá un CUIT de 11 dígitos.');
      return;
    }
    setConsultingArca(true);
    setArcaError(null);
    setArcaFound(null);
    try {
      const result = await consultaPadron(normalizeCuitDigits(draft.supplierTaxId), branchId);
      patch({ supplierTaxId: result.cuit, supplierName: result.razonSocial });
      setArcaFound(
        result.sandbox
          ? `${result.razonSocial} · Modo prueba, el nombre no es el de ARCA`
          : result.razonSocial
      );
    } catch (err: unknown) {
      setArcaError(err instanceof Error ? err.message : 'No se pudo consultar ARCA.');
    } finally {
      setConsultingArca(false);
    }
  };
  const doc = purchaseDocType(draft.docType);
  const discriminates = docTypeDiscriminatesIva(draft.docType);

  const fiscalTotal = useMemo(() => expenseFiscalDraftTotal(draft), [draft]);
  const mismatch =
    hasDoc && expenseAmount != null && fiscalTotal > 0 &&
    Math.abs(fiscalTotal - expenseAmount) > 0.5;

  const patchAlicuota = (index: number, p: Partial<{ rateId: number; baseImpText: string; ivaText: string }>) =>
    patch({ alicuotas: draft.alicuotas.map((a, i) => (i === index ? { ...a, ...p } : a)) });

  return (
    <ModalFormSection title="Comprobante fiscal">
      <ExpenseFormChipRow
        options={[
          { id: 'sin', label: 'Sin comprobante fiscal' },
          { id: 'con', label: 'Tiene comprobante fiscal' },
        ]}
        value={hasDoc ? 'con' : 'sin'}
        onChange={(v) =>
          patch({
            docType: v === 'con' ? docCodeFor('A', 'factura') : null,
          })
        }
        disabled={submitting}
        testIDPrefix={`${testIDPrefix}.toggle`}
      />

      {hasDoc ? (
        <View style={styles.fiscalBody}>
          <View style={styles.row}>
            <View style={styles.col}>
              <ModalFormField label="Tipo" required>
                <ExpenseFormChipRow
                  options={KIND_OPTIONS}
                  value={doc?.kind ?? 'factura'}
                  onChange={(kind) =>
                    patch({ docType: docCodeFor(doc?.docClass ?? 'A', kind) })
                  }
                  disabled={submitting}
                  testIDPrefix={`${testIDPrefix}.kind`}
                />
              </ModalFormField>
            </View>
            <View style={styles.col}>
              <ModalFormField label="Clase" required>
                <ExpenseFormChipRow
                  options={DOC_CLASSES.map((c) => ({ id: c, label: c }))}
                  value={doc?.docClass ?? 'A'}
                  onChange={(c) =>
                    patch({ docType: docCodeFor(c, doc?.kind ?? 'factura') })
                  }
                  disabled={submitting}
                  testIDPrefix={`${testIDPrefix}.class`}
                />
              </ModalFormField>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.col}>
              <ModalFormField label="Punto de venta" required>
                <TextInput
                  style={inputStyle}
                  value={draft.pvText}
                  onChangeText={(v) => patch({ pvText: v })}
                  placeholder="Ej. 3"
                  placeholderTextColor={ticketColors.ink3}
                  keyboardType="number-pad"
                  editable={!submitting}
                  testID={`${testIDPrefix}.pv`}
                />
              </ModalFormField>
            </View>
            <View style={styles.col}>
              <ModalFormField label="Número" required>
                <TextInput
                  style={inputStyle}
                  value={draft.numberText}
                  onChangeText={(v) => patch({ numberText: v })}
                  placeholder="Ej. 00012345"
                  placeholderTextColor={ticketColors.ink3}
                  keyboardType="number-pad"
                  editable={!submitting}
                  testID={`${testIDPrefix}.number`}
                />
              </ModalFormField>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.col}>
              <ModalFormField label="CUIT del emisor" required>
                <TextInput
                  style={inputStyle}
                  value={draft.supplierTaxId}
                  onChangeText={(v) => {
                    setArcaFound(null);
                    patch({ supplierTaxId: v });
                  }}
                  placeholder="Ej. 30-70823456-7"
                  placeholderTextColor={ticketColors.ink3}
                  keyboardType="number-pad"
                  editable={!submitting}
                  testID={`${testIDPrefix}.cuit`}
                />
                <TouchableOpacity
                  style={[posUi.secondaryButton, styles.arcaButton, consultingArca && posUi.disabledCard]}
                  onPress={() => void handleConsultarArca()}
                  disabled={consultingArca || submitting}
                  accessibilityRole="button"
                  accessibilityLabel="Consultar CUIT en ARCA"
                  testID={`${testIDPrefix}.consultarArca`}
                >
                  {consultingArca ? (
                    <ActivityIndicator color={ticketColors.ink} />
                  ) : (
                    <Text style={posUi.secondaryButtonText}>Consultar ARCA</Text>
                  )}
                </TouchableOpacity>
                <Text style={styles.hintText}>
                  Trae la razón social desde el padrón y valida que el CUIT exista.
                </Text>
                {arcaFound ? (
                  <Text style={styles.arcaFoundText} testID={`${testIDPrefix}.arcaFound`}>
                    Encontrado: {arcaFound}
                  </Text>
                ) : null}
                {arcaError ? (
                  <View style={modalExperience.warningPanel} testID={`${testIDPrefix}.arcaError`}>
                    <Text style={modalExperience.stateText}>{arcaError}</Text>
                  </View>
                ) : null}
              </ModalFormField>
            </View>
            <View style={styles.col}>
              <ModalFormField label="Razón social del emisor">
                <TextInput
                  style={inputStyle}
                  value={draft.supplierName}
                  onChangeText={(v) => patch({ supplierName: v })}
                  placeholder="Como figura en el comprobante"
                  placeholderTextColor={ticketColors.ink3}
                  editable={!submitting}
                  testID={`${testIDPrefix}.supplierName`}
                />
              </ModalFormField>
            </View>
          </View>

          {discriminates ? (
            <ModalFormField label="IVA discriminado por alícuota">
              {draft.alicuotas.map((a, i) => (
                <View key={i} style={styles.alicuotaRow}>
                  <View style={styles.alicuotaRate}>
                    <ExpenseFormChipRow
                      options={ALICUOTA_OPTIONS}
                      value={String(a.rateId)}
                      onChange={(id) => patchAlicuota(i, { rateId: Number(id) })}
                      disabled={submitting}
                      testIDPrefix={`${testIDPrefix}.ali.${i}.rate`}
                    />
                  </View>
                  <TextInput
                    style={[inputStyle, styles.alicuotaInput]}
                    value={a.baseImpText}
                    onChangeText={(v) => patchAlicuota(i, { baseImpText: v })}
                    placeholder="Neto"
                    placeholderTextColor={ticketColors.ink3}
                    keyboardType="decimal-pad"
                    editable={!submitting}
                    testID={`${testIDPrefix}.ali.${i}.neto`}
                  />
                  <TextInput
                    style={[inputStyle, styles.alicuotaInput]}
                    value={a.ivaText}
                    onChangeText={(v) => patchAlicuota(i, { ivaText: v })}
                    placeholder="IVA"
                    placeholderTextColor={ticketColors.ink3}
                    keyboardType="decimal-pad"
                    editable={!submitting}
                    testID={`${testIDPrefix}.ali.${i}.iva`}
                  />
                  {draft.alicuotas.length > 1 ? (
                    <TouchableOpacity
                      onPress={() =>
                        patch({ alicuotas: draft.alicuotas.filter((_, j) => j !== i) })
                      }
                      disabled={submitting}
                      accessibilityRole="button"
                      accessibilityLabel="Quitar alícuota"
                      style={styles.alicuotaRemove}
                    >
                      <Ionicons name="close" size={18} color={ticketColors.bad} />
                    </TouchableOpacity>
                  ) : null}
                </View>
              ))}
              <TouchableOpacity
                onPress={() =>
                  patch({ alicuotas: [...draft.alicuotas, { rateId: 5, baseImpText: '', ivaText: '' }] })
                }
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Agregar otra alícuota"
                testID={`${testIDPrefix}.ali.add`}
                style={styles.alicuotaAdd}
              >
                <Ionicons name="add" size={16} color={ticketColors.gold} />
                <Text style={styles.alicuotaAddText}>Agregar alícuota</Text>
              </TouchableOpacity>

              <View style={styles.row}>
                <View style={styles.col}>
                  <ModalFormField label="Exentos">
                    <TextInput
                      style={inputStyle}
                      value={draft.opExText}
                      onChangeText={(v) => patch({ opExText: v })}
                      placeholder="0"
                      placeholderTextColor={ticketColors.ink3}
                      keyboardType="decimal-pad"
                      editable={!submitting}
                      testID={`${testIDPrefix}.exento`}
                    />
                  </ModalFormField>
                </View>
                <View style={styles.col}>
                  <ModalFormField label="No gravados">
                    <TextInput
                      style={inputStyle}
                      value={draft.totConcText}
                      onChangeText={(v) => patch({ totConcText: v })}
                      placeholder="0"
                      placeholderTextColor={ticketColors.ink3}
                      keyboardType="decimal-pad"
                      editable={!submitting}
                      testID={`${testIDPrefix}.noGravado`}
                    />
                  </ModalFormField>
                </View>
              </View>
            </ModalFormField>
          ) : (
            <Text style={styles.hintText}>
              Las facturas B/C no discriminan IVA — va el total nomás, sin desglose.
            </Text>
          )}

          <ModalFormField label="Percepciones sufridas">
            <View style={styles.percepGrid}>
              {(
                [
                  ['percIvaText', 'IVA'],
                  ['percIibbText', 'Ingresos Brutos'],
                  ['percNacionalesText', 'Nacionales'],
                  ['percMunicipalesText', 'Municipales'],
                ] as const
              ).map(([key, label]) => (
                <View key={key} style={styles.percepItem}>
                  <Text style={styles.percepLabel}>{label}</Text>
                  <TextInput
                    style={inputStyle}
                    value={draft[key]}
                    onChangeText={(v) => patch({ [key]: v })}
                    placeholder="0"
                    placeholderTextColor={ticketColors.ink3}
                    keyboardType="decimal-pad"
                    editable={!submitting}
                    testID={`${testIDPrefix}.perc.${key}`}
                  />
                </View>
              ))}
            </View>
          </ModalFormField>

          {mismatch ? (
            <View style={modalExperience.warningPanel} testID={`${testIDPrefix}.mismatch`}>
              <Text style={styles.hintText}>
                El total del comprobante ({formatMoney(fiscalTotal)}) no coincide con el monto
                del gasto ({formatMoney(expenseAmount ?? 0)}). Revisá que esté bien cargado.
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}
    </ModalFormSection>
  );
}

const styles = StyleSheet.create({
  fiscalBody: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  col: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 200,
  },
  alicuotaRow: {
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  alicuotaRate: {
    marginBottom: spacing.xs,
  },
  alicuotaInput: {
    marginBottom: spacing.xs,
  },
  alicuotaRemove: {
    alignSelf: 'flex-end',
    padding: spacing.xs,
  },
  alicuotaAdd: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    minHeight: 44,
    paddingVertical: spacing.xs,
  },
  alicuotaAddText: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  percepGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  percepItem: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 140,
    gap: spacing.xs,
  },
  percepLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  hintText: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  arcaButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  arcaFoundText: {
    ...typography.caption,
    color: ticketColors.ok,
    lineHeight: 18,
  },
});
